import { proxy } from 'valtio'
import lodashThrottle from 'lodash/throttle'
import { type ApiCallType } from '../../../../../shared/types/ApiCallType'
import { buildAxiosInstance } from '../../../../../shared/utils/apiUtils'
import { convertFromCentValue } from '../../../../../shared/utils/formattingUtils'
import { type TimeIntervals } from '../timeIntervalProxy'
import { type LineDataArrayObject, parseGraphData } from './Components/TransactionLineChart'

interface Response {
  data: {
    rptSalesKpis: KPISummaryType[]
    pagination: {
      count: number
    }
  } | null
  error: string | null
}
export interface KPISummaryType {
  merchantId: number
  timeInterval: string
  endDate: string
  salesDollarsCents: number
  salesDollarsCurrencyType: string
  refundDollarsCents: number
  refundDollarsCurrencyType: string
  salesTxnsCount: number
  refundTxnsCount: number
}

export interface RecentSalesProxyType {
  apiCall: ApiCallType
  cardSaleSummaries: KPISummaryType[]
  mostRecentSummary: KPISummaryType
  parsedGraphData: LineDataArrayObject[]
}

export const recentSalesProxy: RecentSalesProxyType = proxy({
  apiCall: {
    error: null,
    loading: false
  },
  cardSaleSummaries: [],
  mostRecentSummary: {
    endDate: '',
    merchantId: 0,
    refundDollarsCents: 0,
    refundDollarsCurrencyType: 'USD',
    refundTxnsCount: 0,
    salesDollarsCents: 0,
    salesDollarsCurrencyType: 'USD',
    salesTxnsCount: 0,
    timeInterval: 'WEEKLY'
  },
  parsedGraphData: []
})

export const throttledFetchRecentSales = lodashThrottle(FetchRecentSales, 500, { trailing: false })

export async function FetchRecentSales (interval: TimeIntervals): Promise<void> {
  recentSalesProxy.apiCall.loading = true
  recentSalesProxy.apiCall.error = null
  try {
    const response = await buildAxiosInstance().post<Response>('/api/kpi/retrieve', {
      limit: 12,
      timeInterval: interval
    })
    const kpiData = response.data.data?.rptSalesKpis ?? []

    if (kpiData?.length > 0) {
      kpiData.forEach(record => {
        record.salesDollarsCents = convertFromCentValue(record.salesDollarsCents, record.salesDollarsCurrencyType)
        record.refundDollarsCents = convertFromCentValue(record.refundDollarsCents, record.refundDollarsCurrencyType)
      })
      recentSalesProxy.cardSaleSummaries = kpiData
      recentSalesProxy.mostRecentSummary = kpiData[0]
      recentSalesProxy.parsedGraphData = parseGraphData(kpiData)
    }
  } catch (e) {
    if (e instanceof Error) {
      recentSalesProxy.apiCall.error = e.message
    }
  } finally {
    recentSalesProxy.apiCall.loading = false
  }
}
